import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { arrayMoveImmutable } from "array-move";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import Select from "react-select";
import TimePicker from "react-times";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  changeSettingValue,
  deleteChannel,
  deletePaymentMethod,
  deleteSaleGroup,
  deleteSaleType,
  deleteServicePeriod,
  getFinanceSaleSettings,
  getGroupAccList,
  saveChannel,
  savePaymentMethod,
  saveSaleGroup,
  saveSaleType,
  saveServicePeriod,
  updateMatrixFinanceSale,
  updatePmOrdering,
} from "../../../../../Redux/actions";
import {
  changeDateFormat,
  checkArray,
  checkNull,
  parseStringToDate,
} from "../../../../../helpers";
import ModelContainer from "../../../../Components/ModelContainer";
import ErrorContainer from "../../../../Components/ErrorContainer";
import moment from "moment";

const channelSchema = yup.object({
  id: yup.string().nullable(),
  sales_type_id: yup.string().required(),
  channel_name: yup.string().required(),
  integration_type: yup.string().required(),
  integration_name: yup.string().required(),
});
const saleGroupSchema = yup.object({
  id: yup.string().nullable(),
  sales_group_name_id: yup.string().required(),
  action_id: yup.string().nullable(),
});
const saleTypeSchema = yup.object({
  id: yup.string().nullable(),
  type_name: yup.string().required(),
});
const servicePeriodSchema = yup.object({
  id: yup.string().nullable(),
  period_name: yup.string().required(),
  start_time: yup.string().required(),
  end_time: yup.string().required(),
  period_days: yup.array().required().min(1),
});
const payMethodSchema = yup
  .object({
    id: yup.string().nullable(),
    method_name: yup.string().required(),
    method_type_id: yup.string().required(),
    customer_id: yup.string().when("method_type_id", {
      is: (val) => ["5"].includes(val),
      then: yup.string().required(),
    }),
    voucher_group_id: yup.string().when("method_type_id", {
      is: (val) => ["5"].includes(val),
      then: yup.string().required(),
    }),
    default_ar_debit_account_id: yup.string().when("method_type_id", {
      is: (val) => ["5"].includes(val),
      then: yup.string().required(),
    }),
    default_customer_payment_account_id: yup.string().when("method_type_id", {
      is: (val) => ["5"].includes(val),
      then: yup.string().required(),
    }),
    fixed_amount_for_vouchers: yup.string().when("method_type_id", {
      is: (val) => ["5"].includes(val),
      then: yup.string().required(),
    }),
    voucher_value: yup
      .string()
      .when(["method_type_id", "fixed_amount_for_vouchers"], {
        is: (methodTypeVal, fixedAmtVal) =>
          ["5"].includes(methodTypeVal) && ["yes"].includes(fixedAmtVal),
        then: yup.string().required(),
      }),
    capture_excess_amount_other_income: yup.string().when("method_type_id", {
      is: (val) => ["5"].includes(val),
      then: yup.string().required(),
    }),
    method_fee: yup.string().when("method_type_id", {
      is: (val) => !["5", "6"].includes(val),
      then: yup.string().required(),
    }),
    refundable: yup.string().when("method_type_id", {
      is: (val) => !["5", "6"].includes(val),
      then: yup.string().required(),
    }),
    default_eod_debit_account_id: yup.string().when("method_type_id", {
      is: (val) => !["5", "6"].includes(val),
      then: yup.string().required(),
    }),
    default_debit_account_id: yup.string().when("method_type_id", {
      is: (val) => ["1", "2", "3", "4", "6"].includes(val),
      then: yup.string().required(),
    }),
    same_day_clearing: yup.string().when("method_type_id", {
      is: (val) => !["5", "6"].includes(val),
      then: yup.string().required(),
    }),
    clearing_delay: yup.string().when(["method_type_id", "same_day_clearing"], {
      is: (methodTypeVal, sameDayVal) =>
        !["5", "6"].includes(methodTypeVal) && ["2"].includes(sameDayVal),
      then: yup.string().required(),
    }),
    ap_voucher_type_id: yup.string().when("method_type_id", {
      is: (val) => ["6"].includes(val),
      then: yup.string().required(),
    }),
    voucher_group_id: yup.string().when("method_type_id", {
      is: (val) => ["6"].includes(val),
      then: yup.string().required(),
    }),
    ap_credit_account_id: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && ["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    funds_debit_account_id: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && ["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    convert_unredeemed_balance: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && ["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    other_income_account_expiry_id: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && ["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    voucher_partner: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && !["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    voucher_series_id: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && !["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    voucher_total_value: yup
      .string()
      .when(["method_type_id", "ap_voucher_type_id"], {
        is: (methodTypeVal, apVoucherVal) =>
          ["6"].includes(methodTypeVal) && ["1", "2"].includes(apVoucherVal),
        then: yup.string().required(),
      }),
    denomination_table: yup.array().of(
      yup.object().shape({
        add_new: yup.string().nullable(),
        denom_value: yup.string(),
        denom_number: yup.number(),
        total_denom: yup.number(),
        expiry_date: yup.string(),
        ar_invoice_id: yup.string(),
      })
    ),
  })
  .required();

const Sales = (params) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    finance_sale_details,
    payMethodModal,
    servicePeriodModal,
    group_account,
    is_save_payment_method_loading,
    is_service_period_loading,
    saleTypeModal,
    is_sale_type_loading,
    saleGroupModal,
    is_sale_group_loading,
    channelModal,
    is_channel_loading,
  } = useSelector((state) => state.settingReducer);
  useEffect(() => {
    dispatch(getFinanceSaleSettings());

    return () => {
      dispatch(changeSettingValue("finance_sale_details", null));
      dispatch(changeSettingValue("payMethodModal", false));
      dispatch(changeSettingValue("servicePeriodModal", false));
      dispatch(changeSettingValue("saleTypeModal", false));
    };
  }, []);

  /* Payment Method */
  const [paymentMethodArr, setPaymentMethodArr] = useState([]);
  const getPayMethodActionBtn = (payMethod) => {
    let res = null;
    if (payMethod?.parent_id == 0 && payMethod?.set == 1) {
      res = null;
    } else if (payMethod?.editable == 0 || payMethod?.parent_id != 0) {
      res = (
        <>
          <a
            className="editPm circle_icon"
            onClick={() => editPayMethodHandler(payMethod)}
          >
            {" "}
            <i className="iconSprites edit-icon"></i>{" "}
          </a>

          <div className="circle_icon no-hover"> </div>
        </>
      );
    } else {
      res = (
        <>
          <a
            className="editPm circle_icon"
            onClick={() => editPayMethodHandler(payMethod)}
          >
            {" "}
            <i className="iconSprites edit-icon"></i>{" "}
          </a>

          <a
            className="deletePm circle_icon"
            onClick={() => deletePayMethodHandler(payMethod.id)}
          >
            {" "}
            <i className="iconSprites delete-icon"></i>{" "}
          </a>
        </>
      );
    }
    return res;
  };

  const SortableItem = ({ value, valIdx }) => (
    <tr className="pmitem_row">
      {console.log("valIdx", valIdx)}
      <td>{value?.method_name} </td>
      <td>{finance_sale_details?.methodTypes[value?.method_type_id]} </td>
      <td>{parseFloat(value?.method_fee ?? 0).toFixed(2)}% </td>
      <td> {value?.refundable == 1 ? "Yes" : "No"} </td>
      <td></td>
      <td className="right-arrows-sec">
        {getPayMethodActionBtn(value)}
        <div className="event-time-edit up-down-btn-parent p0">
          {valIdx !== 0 && (
            <div className="circle_icon no-hover">
              {" "}
              <a
                className="upParent arrow_hide"
                onClick={() => {
                  setPaymentMethodArr(
                    arrayMoveImmutable(paymentMethodArr, valIdx, valIdx - 1)
                  );
                }}
              >
                {" "}
                <i className="icon-arrow-up"></i>{" "}
              </a>{" "}
            </div>
          )}
          {valIdx !== paymentMethodArr.length - 1 && (
            <a
              className="downParent"
              onClick={() => {
                console.log("paymentMethodArr", valIdx);
                setPaymentMethodArr(
                  arrayMoveImmutable(paymentMethodArr, valIdx, valIdx + 1)
                );
              }}
            >
              {" "}
              <i className="icon-arrow-down"></i>{" "}
            </a>
          )}
        </div>
      </td>
    </tr>
  );

  const SortableList = ({ items }) => {
    return (
      <tbody>
        {items.map((value, index) => (
          <SortableItem key={`item-${index}`} valIdx={index} value={value} />
        ))}
      </tbody>
    );
  };
  useEffect(() => {
    setPaymentMethodArr(
      checkArray(finance_sale_details?.paymentMethods)
        ? finance_sale_details?.paymentMethods.sort(function (a, b) {
            return a.sort - b.sort;
          })
        : []
    );
    if (
      checkArray(finance_sale_details?.theSelectedMatrices?.[1]) &&
      checkNull(finance_sale_details?.theMatrices?.[1], false)
    ) {
      let selectedpaySubInput = [];

      finance_sale_details?.theSelectedMatrices?.[1].map((matrix) => {
        if (
          Object.keys(finance_sale_details?.theMatrices?.[1]).findIndex(
            (p) => p == matrix
          ) > -1
        ) {
          selectedpaySubInput.push({
            value: `${matrix}`,
            label: finance_sale_details?.theMatrices?.[1]?.[matrix],
          });
        }
      });
      setPayMethodSubInput(selectedpaySubInput);
    }
    if (
      checkArray(finance_sale_details?.theSelectedMatrices?.[2]) &&
      checkNull(finance_sale_details?.theMatrices?.[2], false)
    ) {
      let selectedserviceSubInput = [];

      finance_sale_details?.theSelectedMatrices?.[2].map((matrix) => {
        if (
          Object.keys(finance_sale_details?.theMatrices?.[2]).findIndex(
            (p) => p == matrix
          ) > -1
        ) {
          selectedserviceSubInput.push({
            value: `${matrix}`,
            label: finance_sale_details?.theMatrices?.[2]?.[matrix],
          });
        }
      });
      console.log(
        "selectedserviceSubInput:",
        selectedserviceSubInput,
        finance_sale_details?.theSelectedMatrices?.[2]
      );
      setServicePeriodSubInput(selectedserviceSubInput);
    }
    if (
      checkArray(finance_sale_details?.theSelectedMatrices?.[3]) &&
      checkNull(finance_sale_details?.theMatrices?.[3], false)
    ) {
      let selectedpaySubInput = [];

      finance_sale_details?.theSelectedMatrices?.[3].map((matrix) => {
        if (
          Object.keys(finance_sale_details?.theMatrices?.[3]).findIndex(
            (p) => p == matrix
          ) > -1
        ) {
          selectedpaySubInput.push({
            value: `${matrix}`,
            label: finance_sale_details?.theMatrices?.[3]?.[matrix],
          });
        }
      });
      setSaleTypeSubInput(selectedpaySubInput);
    }
    setSaleGroupSubInput([{ value: 1, label: "Payment Methods" }]);
  }, [finance_sale_details]);

  /* Add Payment Method */
  const initialpayMethodForm = {
    id: "",
    method_name: "",
    method_type_id: "",
    customer_id: "",
    voucher_group_id: "",
    default_ar_debit_account_id: "",
    default_customer_payment_account_id: "",
    fixed_amount_for_vouchers: "",
    voucher_value: "",
    capture_excess_amount_other_income: "",
    method_fee: "",
    refundable: "",
    default_eod_debit_account_id: "",
    default_debit_account_id: "",
    same_day_clearing: "",
    clearing_delay: "",
    ap_voucher_type_id: "",
    voucher_group_id: "",
    ap_credit_account_id: "",
    funds_debit_account_id: "",
    convert_unredeemed_balance: "",
    other_income_account_expiry_id: "",
    voucher_partner: "",
    voucher_series_id: "",
    voucher_total_value: "",
    denomination_table: [],
  };
  const {
    register: payMethodReg,
    formState: { errors: payMethodErrors },
    handleSubmit: payMethodSubmit,
    control,
    setValue: payMethodSetValue,
    reset,
  } = useForm({
    resolver: yupResolver(payMethodSchema),
    defaultValues: initialpayMethodForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [payMethodInputForm, setPayMethodInputForm] =
    useState(initialpayMethodForm);
  const [payMethodSubInput, setPayMethodSubInput] = useState([]);

  const payMethodSubInputHandler = (values) => {
    setPayMethodSubInput(values);
    if (checkArray(values)) {
      const updateMatData = {
        number: 1,
        selected: values.map((input) => input.value),
      };
      dispatch(updateMatrixFinanceSale(updateMatData));
    } else {
      dispatch(
        updateMatrixFinanceSale({
          number: 1,
          selected: [],
        })
      );
    }
  };

  const changePayMethodInputHandler = (input, val) => {
    if (input == "voucher_group_id" && checkNull(val, false)) {
      dispatch(getGroupAccList(val));
    }
    payMethodSetValue(input, val);
    setPayMethodInputForm((prevState) => ({ ...prevState, [input]: val }));
  };
  const initialPayMethodModalState = (initailVal = initialpayMethodForm) => {
    setPayMethodInputForm(initailVal);
    reset(initailVal);
  };
  const closePayMethodModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("payMethodModal", value));
      initialPayMethodModalState();
    },
    [payMethodModal]
  );
  const submitPayMethodForm = (data) => {
    if (checkArray(data?.denomination_table)) {
      data.denomination_table = data.denomination_table.map((denom) => {
        return {
          ...denom,
          expiry_date: moment(denom?.expiry_date).format("yyyy-MM-DD"),
        };
      });
    }
    console.log("submitPayMethodForm", data);
    dispatch(savePaymentMethod(data));
    // closeModal(false);
  };
  const deletePayMethodHandler = (id) => {
    dispatch(deletePaymentMethod({ id }));
  };

  const editPayMethodHandler = (payMethod) => {
    closePayMethodModal(true);
    const editValues = {
      id: payMethod?.id ?? "",
      method_type_id: checkNull(payMethod?.method_type_id) ?? "",
      customer_id: checkNull(payMethod?.customer_id) ?? "",
      voucher_group_id: checkNull(payMethod?.voucher_group_id) ?? "",
      default_ar_debit_account_id:
        checkNull(payMethod?.default_ar_debit_account_id) ?? "",
      default_customer_payment_account_id:
        checkNull(payMethod?.default_customer_payment_account_id) ?? "",
      fixed_amount_for_vouchers:
        checkNull(payMethod?.fixed_amount_for_vouchers) ?? "",
      voucher_value: checkNull(payMethod?.voucher_value) ?? "",
      capture_excess_amount_other_income:
        checkNull(payMethod?.capture_excess_amount_other_income) ?? "",
      method_fee: checkNull(payMethod?.method_fee) ?? "",
      refundable: checkNull(payMethod?.refundable) ?? "",
      default_eod_debit_account_id:
        checkNull(payMethod?.default_eod_debit_account_id) ?? "",
      default_debit_account_id:
        checkNull(payMethod?.default_debit_account_id) ?? "",
      same_day_clearing: checkNull(payMethod?.same_day_clearing) ?? "",
      clearing_delay: checkNull(payMethod?.clearing_delay) ?? "",
      ap_voucher_type_id: checkNull(payMethod?.ap_voucher_type_id) ?? "",
      voucher_group_id: checkNull(payMethod?.voucher_group_id) ?? "",
      ap_credit_account_id: checkNull(payMethod?.ap_credit_account_id) ?? "",
      funds_debit_account_id:
        checkNull(payMethod?.funds_debit_account_id) ?? "",
      convert_unredeemed_balance:
        checkNull(payMethod?.convert_unredeemed_balance) ?? "",
      other_income_account_expiry_id:
        checkNull(payMethod?.other_income_account_expiry_id) ?? "",
      voucher_partner: checkNull(payMethod?.voucher_partner) ?? "",
      voucher_series_id: checkNull(payMethod?.voucher_series_id) ?? "",
      voucher_total_value: checkNull(payMethod?.voucher_total_value) ?? "",
      denomination_table: [],
    };
    initialPayMethodModalState(editValues);
  };

  const checkPayMethodId = (ids = []) => {
    return ids.includes((payMethodInputForm?.method_type_id).toString());
  };

  const addDenominationHandler = () => {
    const newDenom = [
      ...payMethodInputForm.denomination_table,
      {
        id: "",
        denom_value: "",
        denom_number: 0,
        total_denom: 0,
        expiry_date: "",
        ar_invoice_id: "",
      },
    ];
    changePayMethodInputHandler("denomination_table", newDenom);
  };

  const calculateVoucherTotal = (newDenom) => {
    if (checkArray(newDenom)) {
      let vouTotal = 0;
      newDenom.map((denom) => {
        if (checkNull(denom?.total_denom, false)) {
          vouTotal += parseFloat(denom?.total_denom);
        }
      });
      changePayMethodInputHandler(
        "voucher_total_value",
        `${parseFloat(vouTotal).toFixed(2)}`
      );
    }
  };

  const changeDenominationHandler = (index, type, value) => {
    const checkSubDenom = payMethodInputForm?.denomination_table?.[index];
    if (checkNull(checkSubDenom, false)) {
      const newSubDenom = { ...checkSubDenom, [type]: value };
      if (
        checkNull(newSubDenom.denom_value, false) &&
        checkNull(newSubDenom.denom_number, false) &&
        checkNull(newSubDenom.expiry_date, false) &&
        checkNull(newSubDenom.ar_invoice_id, false)
      ) {
        newSubDenom.total_denom = parseFloat(
          parseFloat(newSubDenom.denom_value) *
            parseFloat(newSubDenom.denom_number)
        ).toFixed(2);
      }
      const newDenom = [...payMethodInputForm.denomination_table];
      newDenom[index] = newSubDenom;
      changePayMethodInputHandler("denomination_table", newDenom);
      calculateVoucherTotal(newDenom);
    }
  };

  const saveOrderHandler = () => {
    let newOrder = [];
    if (paymentMethodArr.length > 0) {
      paymentMethodArr.map((item, itemIdx) => {
        newOrder.push({
          pm_id: item.id,
          merchant_id: item.merchant_id,
          sort: itemIdx + 1,
        });
      });
    }
    dispatch(updatePmOrdering({ payment_methods: newOrder }));
  };

  // add service period
  const [weeklyArr, setWeeklyArr] = useState([]);
  useEffect(() => {
    if (checkArray(finance_sale_details?.weekly)) {
      setWeeklyArr(
        finance_sale_details?.weekly.map((week, weekIdx) => {
          return { value: weekIdx, label: week };
        })
      );
    }
  }, [finance_sale_details]);

  const initialservicePeriodForm = {
    id: "",
    period_name: "",
    start_time: "",
    end_time: "",
    period_days: [],
  };
  const {
    register: servicePeriodReg,
    formState: { errors: servicePeriodErrors },
    handleSubmit: servicePeriodSubmit,
    setValue: servicePeriodSetValue,
    reset: servicePeriodReset,
  } = useForm({
    resolver: yupResolver(servicePeriodSchema),
    defaultValues: initialservicePeriodForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [servicePeriodInputForm, setServicePeriodInputForm] = useState(
    initialservicePeriodForm
  );

  const [servicePeriodSubInput, setServicePeriodSubInput] = useState([]);

  const servicePeriodSubInputHandler = (values) => {
    setServicePeriodSubInput(values);
    if (checkArray(values)) {
      const updateMatData = {
        number: 2,
        selected: values.map((input) => input.value),
      };
      dispatch(updateMatrixFinanceSale(updateMatData));
    } else {
      dispatch(
        updateMatrixFinanceSale({
          number: 2,
          selected: [],
        })
      );
    }
  };

  const changeServicePeriodInputHandler = (input, val) => {
    servicePeriodSetValue(input, val);
    setServicePeriodInputForm((prevState) => ({ ...prevState, [input]: val }));
  };
  const initialServicePeriodModalState = (
    initailVal = initialservicePeriodForm
  ) => {
    setServicePeriodInputForm(initailVal);
    servicePeriodReset(initailVal);
  };
  const closeServicePeriodModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("servicePeriodModal", value));
      initialServicePeriodModalState();
    },
    [servicePeriodModal]
  );
  const submitServicePeriodForm = (data) => {
    console.log("submitServicePeriodForm", data);
    if (checkArray(data?.period_days)) {
      data.period_days = data?.period_days.map((period) => period.value);
    }
    dispatch(saveServicePeriod(data));
    // closeModal(false);
  };
  const deleteServicePeriodHandler = (id) => {
    dispatch(deleteServicePeriod({ id }));
  };

  const editServicePeriodHandler = (servicePeriod) => {
    closeServicePeriodModal(true);
    const selectedDays = [];
    if (checkArray(servicePeriod?.period_days)) {
      servicePeriod?.period_days.map((day) => {
        const dayIndx = weeklyArr.findIndex((p) => p.label == day);
        if (dayIndx > -1) {
          selectedDays.push({ value: weeklyArr?.[dayIndx]?.value, label: day });
        }
      });
    }
    const editValues = {
      id: servicePeriod?.id ?? "",
      period_name: checkNull(servicePeriod?.period_name) ?? "",
      period_days: selectedDays,
      start_time: checkNull(servicePeriod?.start_time, false)
        ? parseStringToDate(servicePeriod?.start_time, "HH:mm:ss", "hh:mm A")
        : "",
      end_time: checkNull(servicePeriod?.end_time)
        ? parseStringToDate(servicePeriod?.end_time, "HH:mm:ss", "hh:mm A")
        : "",
    };
    initialServicePeriodModalState(editValues);
  };

  const getServicePeriodAction = (period) => {
    let res = null;
    if (period?.period != 0 && period?.set == 1) {
      res = null;
    } else {
      res = (
        <>
          <a
            href="#_"
            class="editSp circle_icon"
            onClick={() => editServicePeriodHandler(period)}
          >
            <i class="iconSprites edit-icon"></i>
          </a>
          {period?.parent_id == 0 && (
            <a
              href="#_"
              class="deleteSp circle_icon"
              onClick={() => deleteServicePeriodHandler(period?.id)}
            >
              <i class="iconSprites delete-icon"></i>
            </a>
          )}
        </>
      );
    }
    return res;
  };

  /* Sales Type */
  const initialsaleTypeForm = {
    id: "",
    type_name: "",
  };
  const {
    register: saleTypeReg,
    formState: { errors: saleTypeErrors },
    handleSubmit: saleTypeSubmit,
    setValue: saleTypeSetValue,
    reset: saleTypeReset,
  } = useForm({
    resolver: yupResolver(saleTypeSchema),
    defaultValues: initialsaleTypeForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [saleTypeInputForm, setSaleTypeInputForm] =
    useState(initialsaleTypeForm);
  const changeSaleTypeInputHandler = (input, val) => {
    saleTypeSetValue(input, val);
    setSaleTypeInputForm((prevState) => ({ ...prevState, [input]: val }));
  };

  const [saleTypeSubInput, setSaleTypeSubInput] = useState([]);
  const saleTypeSubInputHandler = (values) => {
    setSaleTypeSubInput(values);
    if (checkArray(values)) {
      const updateMatData = {
        number: 3,
        selected: values.map((input) => input.value),
      };
      dispatch(updateMatrixFinanceSale(updateMatData));
    } else {
      dispatch(
        updateMatrixFinanceSale({
          number: 3,
          selected: [],
        })
      );
    }
  };

  const initialSaleTypeModalState = (initailVal = initialsaleTypeForm) => {
    setSaleTypeInputForm(initailVal);
    saleTypeReset(initailVal);
  };
  const closeSaleTypeModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("saleTypeModal", value));
      initialSaleTypeModalState();
    },
    [saleTypeModal]
  );
  const submitSaleTypeForm = (data) => {
    console.log("submitSaleTypeForm", data);
    dispatch(saveSaleType(data));
  };
  const deleteSaleTypeHandler = (id) => {
    dispatch(deleteSaleType({ id }));
  };

  const editSaleTypeHandler = (saleType) => {
    closeSaleTypeModal(true);
    const editValues = {
      id: saleType?.id ?? "",
      type_name: checkNull(saleType?.type_name) ?? "",
    };
    initialSaleTypeModalState(editValues);
  };

  const getSaleTypeAction = (sale) => {
    let res = null;
    if (sale?.allow_edit == 1) {
      if (sale?.parent_id != 0 && sale?.set == 1) {
        res = null;
      } else {
        res = (
          <>
            <a
              href="#_"
              class="editSp circle_icon"
              onClick={() => editSaleTypeHandler(sale)}
            >
              <i class="iconSprites edit-icon"></i>
            </a>
            {sale?.parent_id == 0 && (
              <a
                href="#_"
                class="deleteSp circle_icon"
                onClick={() => deleteSaleTypeHandler(sale?.id)}
              >
                <i class="iconSprites delete-icon"></i>
              </a>
            )}
          </>
        );
      }
    }
    return res;
  };

  /* Sale Group */
  const initialsaleGroupForm = {
    id: "",
    type_name: "",
    action_id: "",
  };
  const {
    register: saleGroupReg,
    formState: { errors: saleGroupErrors },
    handleSubmit: saleGroupSubmit,
    setValue: saleGroupSetValue,
    reset: saleGroupReset,
  } = useForm({
    resolver: yupResolver(saleGroupSchema),
    defaultValues: initialsaleGroupForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [saleGroupInputForm, setSaleGroupInputForm] =
    useState(initialsaleGroupForm);
  const [saleGroupSubInput, setSaleGroupSubInput] = useState([]);
  const changeSaleGroupInputHandler = (input, val) => {
    saleGroupSetValue(input, val);
    setSaleGroupInputForm((prevState) => ({ ...prevState, [input]: val }));
    if (
      input == "sales_group_name_id" &&
      checkNull(val, false) &&
      checkNull(finance_sale_details?.salesGroupNamesActionsList?.[val], false)
    ) {
      changeSaleGroupInputHandler(
        "action_id",
        `${finance_sale_details?.salesGroupNamesActionsList?.[val]}`
      );
    }
  };
  const initialSaleGroupModalState = (initailVal = initialsaleGroupForm) => {
    setSaleGroupInputForm(initailVal);
    saleGroupReset(initailVal);
  };
  const closeSaleGroupModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("saleGroupModal", value));
      initialSaleGroupModalState();
    },
    [saleGroupModal]
  );
  const submitSaleGroupForm = (data) => {
    console.log("submitSaleGroupForm", data);
    dispatch(saveSaleGroup(data));
  };
  const deleteSaleGroupHandler = (id) => {
    dispatch(deleteSaleGroup({ id }));
  };

  const getSaleGroupAction = (saleGroup) => {
    let res = null;
    if (saleGroup?.editable == 1) {
      res = (
        <a
          href="#_"
          class="deleteSp circle_icon"
          onClick={() => deleteSaleGroupHandler(saleGroup?.id)}
        >
          <i class="iconSprites delete-icon"></i>
        </a>
      );
    }
    return res;
  };

  /* Channels */
  const initialchannelForm = {
    id: "",
    sales_type_id: "",
    channel_name: "",
    integration_type: "",
    integration_name: "",
  };
  const {
    register: channelReg,
    formState: { errors: channelErrors },
    handleSubmit: channelSubmit,
    setValue: channelSetValue,
    reset: channelReset,
  } = useForm({
    resolver: yupResolver(channelSchema),
    defaultValues: initialchannelForm,
    mode: "all",
    reValidateMode: "onChange",
  });
  const [channelInputForm, setChannelInputForm] = useState(initialchannelForm);
  const changeChannelInputHandler = (input, val) => {
    channelSetValue(input, val);
    setChannelInputForm((prevState) => ({ ...prevState, [input]: val }));
  };
  const initialChannelModalState = (initailVal = initialchannelForm) => {
    setChannelInputForm(initailVal);
    channelReset(initailVal);
  };
  const closeChannelModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("channelModal", value));
      initialChannelModalState();
    },
    [channelModal]
  );
  const submitChannelForm = (data) => {
    console.log("submitChannelForm", data);
    dispatch(saveChannel(data));
  };
  const deleteChannelHandler = (id) => {
    dispatch(deleteChannel({ id }));
  };

  const editChannelHandler = (channel) => {
    closeChannelModal(true);
    const editValues = {
      id: channel?.id ?? "",
      sales_type_id: checkNull(channel?.sales_type_id) ?? "",
      channel_name: checkNull(channel?.channel_name) ?? "",
      integration_type: checkNull(channel?.integration_type) ?? "",
      integration_name: checkNull(channel?.integration_name) ?? "",
    };
    initialChannelModalState(editValues);
  };

  const getChannelAction = (channel) => {
    let res = null;
    if (channel?.editable == 1) {
      if (channel?.parent_id != 0 && channel?.set == 1) {
        res = null;
      } else {
        res = (
          <>
            <a
              href="#_"
              class="editSp circle_icon"
              onClick={() => editChannelHandler(channel)}
            >
              <i class="iconSprites edit-icon"></i>
            </a>
            <a
              href="#_"
              class="deleteSp circle_icon"
              onClick={() => deleteChannelHandler(channel?.id)}
            >
              <i class="iconSprites delete-icon"></i>
            </a>
          </>
        );
      }
    }
    return res;
  };

  return (
    <div
      className="tab-pane fade show active"
      id="nav-profile"
      role="tabpanel"
      aria-labelledby="nav-profile-tab"
    >
      <div className="row mx-0">
        <div className="container px-0">
          <div className="accordion" id="accordionPanelsStayOpenExample">
            <div className="accordion-item">
              <h2 className="accordion-header" id="panelsStayOpen-headingOne">
                <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelsStayOpen-collapseOne"
                  aria-expanded="true"
                  aria-controls="panelsStayOpen-collapseOne"
                >
                  <div className="col d-flex align-items-center">
                    <h4 className="event-alert p-0 mb-0">
                      Financial Input and Analysis Preferences{" "}
                    </h4>
                  </div>{" "}
                </button>
              </h2>
              <div
                id="panelsStayOpen-collapseOne"
                className="accordion-collapse collapse show"
                aria-labelledby="panelsStayOpen-headingOne"
              >
                <div className="">
                  <div className="row mx-0">
                    <div className="container px-0">
                      <div className="shadowclass mt-0">
                        <div className="col-lg-8">
                          <div className="select-comparison mt-0 d-flex">
                            <label className="col-md-4 col-12 mr-3">
                              Sales and Transaction Input Type
                            </label>
                            <div className="select-div col-md-4 col-12">
                              <select
                                name="data[sales_financial_source_id]"
                                className="transactionsSource"
                                id="sales_financial_source_id"
                                disabled="disabled"
                                value={finance_sale_details?.salesTransaction?.sales_financial_source?.id??""}
                              >
                                <option value="">Select</option>
                                <option value="1">Manual Input</option>
                                <option value="2">GoGMGo POS</option>
                              </select>{" "}
                            </div>
                          </div>
                          <div className="yes-no-sec d-flex flex-wrap">
                            <label
                              id="req-transaction"
                              className="col-md-4 col-12 mr-3"
                            >
                              Menu Item Input Required? (Recommended)
                            </label>
                            <div className="select-div col-md-4 col-12 pr-4">
                              <select className="select w-100">
                                <option>Yes</option>
                                <option>No</option>
                              </select>
                            </div>
                          </div>
                          <div className="yes-no-sec d-flex flex-wrap">
                            <label
                              id="req-transaction"
                              className="col-md-4 col-12 mr-3"
                            >
                              Transaction Input Required?
                            </label>
                            <div className="select-div col-md-4 col-12 pr-4">
                              <select className="select w-100">
                                <option>Yes</option>
                                <option>No</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="accordion-item">
              <div className="accordion-header" id="panelsStayOpen-headingTwo">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelsStayOpen-collapseTwo"
                  aria-expanded="false"
                  aria-controls="panelsStayOpen-collapseTwo"
                >
                  <h4 className="event-alert p-0 mb-0">Payment Methods</h4>
                </button>
              </div>
              <div
                id="panelsStayOpen-collapseTwo"
                className="accordion-collapse collapse"
                aria-labelledby="panelsStayOpen-headingTwo"
              >
                <div className="">
                  <div className="row mx-0">
                    <div className="container px-0 d-flex">
                      <div className="shadowclass mt-0">
                        <div className="select-comparison d-flex">
                          <label>Input Subsets</label>
                          <div className="FeatureAddDescriptionForm">
                            <Select
                              className="w-100"
                              value={payMethodSubInput}
                              onChange={(values) =>
                                payMethodSubInputHandler(values)
                              }
                              options={
                                checkNull(
                                  finance_sale_details?.theMatrices?.[1],
                                  false
                                )
                                  ? Object.keys(
                                      finance_sale_details?.theMatrices?.[1]
                                    )
                                      .filter((p) => p == 3)
                                      .map((matrix) => {
                                        return {
                                          value: matrix,
                                          label:
                                            finance_sale_details
                                              ?.theMatrices?.[1]?.[matrix],
                                        };
                                      })
                                  : []
                              }
                              isMulti
                            />
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="checklist-table remove-space inventory_table">
                            <thead>
                              <tr>
                                <th className="second-th"> Method Name </th>
                                <th>Method Type</th>
                                <th>Method Fee</th>
                                <th>Refundable?</th>
                                <th></th>
                                <th className="right-arrows-sec"> </th>
                              </tr>
                            </thead>
                            <SortableList items={paymentMethodArr} />
                          </table>

                        </div>
                          <div className="my-3">
                            <button
                              className="btn green-btn-revert add-plus addPm"
                              id="addCount"
                              type="button"
                              onClick={() => closePayMethodModal(true)}
                            >
                              <i className="i-icon"> </i> Add Payment Methods{" "}
                            </button>
                            <button
                              className="btn green-btn-revert"
                              type="button"
                              onClick={() => saveOrderHandler()}
                            >
                              {" "}
                              Save Order{" "}
                            </button>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <div className="accordion-header" id="panelsStayOpen-headingFive">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelsStayOpen-collapseFive"
                  aria-expanded="false"
                  aria-controls="panelsStayOpen-collapseFive"
                >
                  <h4 className="event-alert p-0 mb-0">Service Periods</h4>
                </button>
              </div>
              <div
                id="panelsStayOpen-collapseFive"
                className="accordion-collapse collapse"
                aria-labelledby="panelsStayOpen-headingFive"
              >
                <div className="">
                  <div className="row mx-0">
                    <div className="container px-0 d-flex">
                      <div className="shadowclass mt-0">
                        <div className="select-comparison d-flex">
                          <label>Input Subsets</label>
                          <Select
                            className="w-100"
                            value={servicePeriodSubInput}
                            onChange={(values) =>
                              servicePeriodSubInputHandler(values)
                            }
                            options={
                              checkNull(
                                finance_sale_details?.theMatrices?.[2],
                                false
                              )
                                ? Object.keys(
                                    finance_sale_details?.theMatrices?.[2]
                                  )
                                    .filter((p) => p != 10)
                                    .map((matrix) => {
                                      return {
                                        value: matrix,
                                        label:
                                          finance_sale_details
                                            ?.theMatrices?.[2]?.[matrix],
                                      };
                                    })
                                : []
                            }
                            isMulti
                          />
                        </div>
                        <div className="table-responsive">
                          <table className="checklist-table remove-space inventory_table">
                            <thead>
                              <tr>
                                <th className="second-th" width="">
                                  <a href="#_" className="">
                                    Period Name{" "}
                                  </a>
                                </th>
                                <th>Period Days</th>
                                <th>Period Start</th>
                                <th>Period End</th>
                                <th className="action-td"> Actions </th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkArray(
                                finance_sale_details?.servicePeriods
                              ) &&
                                finance_sale_details?.servicePeriods.map(
                                  (period) => {
                                    return (
                                      <tr key={period?.id}>
                                        <td>{period?.period_name ?? ""} </td>
                                        <td>
                                          {checkArray(period?.period_days)
                                            ? period?.period_days.join(", ")
                                            : ""}
                                        </td>
                                        <td>
                                          {parseStringToDate(
                                            period?.start_time,
                                            "HH:mm:ss",
                                            "hh:m A"
                                          )}{" "}
                                        </td>
                                        <td>
                                          {" "}
                                          {parseStringToDate(
                                            period?.end_time,
                                            "HH:mm:ss",
                                            "hh:m A"
                                          )}
                                        </td>

                                        <td className="d-flex jusitfy-content-end">
                                          {getServicePeriodAction(period)}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                            </tbody>
                          </table>
                        </div>
                          <div className="my-3">
                            <button
                              className="btn green-btn-revert add-plus addSp"
                              id="addCount"
                              type="button"
                              onClick={() => closeServicePeriodModal(true)}
                            >
                              <i className="i-icon"> </i> Add Service Periods{" "}
                            </button>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <div className="accordion-header" id="panelsStayOpen-headingSix">
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelsStayOpen-collapseSix"
                  aria-expanded="false"
                  aria-controls="panelsStayOpen-collapseSix"
                >
                  <h4 className="event-alert p-0 mb-0">Sales Types</h4>
                </button>
              </div>
              <div
                id="panelsStayOpen-collapseSix"
                className="accordion-collapse collapse"
                aria-labelledby="panelsStayOpen-headingSix"
              >
                <div className="">
                  <div className="row mx-0">
                    <div className="container px-0 d-flex">
                      <div className="shadowclass mt-0">
                        <div className="select-comparison d-flex">
                          <label>Input Subsets</label>
                          <Select
                            className="w-100"
                            value={saleTypeSubInput}
                            onChange={(values) =>
                              saleTypeSubInputHandler(values)
                            }
                            options={
                              checkNull(
                                finance_sale_details?.theMatrices?.[3],
                                false
                              )
                                ? Object.keys(
                                    finance_sale_details?.theMatrices?.[3]
                                  )
                                    .filter((p) => p != 2)
                                    .map((matrix) => {
                                      return {
                                        value: matrix,
                                        label:
                                          finance_sale_details
                                            ?.theMatrices?.[3]?.[matrix],
                                      };
                                    })
                                : []
                            }
                            isMulti
                          />
                        </div>
                        <div className="table-responsive">
                          <table className="checklist-table remove-space inventory_table">
                            <thead>
                              <tr>
                                <th className="second-th" width="">
                                  <a href="#_" className="">
                                    Type Name{" "}
                                  </a>
                                </th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th className="col-1 text-center"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkArray(finance_sale_details?.salesTypes) &&
                                finance_sale_details?.salesTypes.map((sale) => {
                                  return (
                                    <tr>
                                      <td>{sale?.type_name} </td>
                                      <td></td>
                                      <td></td>
                                      <td></td>
                                      <td></td>
                                      <td className="">
                                        {getSaleTypeAction(sale)}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                          <div className="my-3">
                            <button
                              className="btn green-btn-revert add-plus"
                              id="addCount"
                              type="button"
                              onClick={() => closeSaleTypeModal(true)}
                            >
                              <i className="i-icon"> </i> Add Sales Types{" "}
                            </button>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <div
                className="accordion-header"
                id="panelsStayOpen-headingSeven"
              >
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelsStayOpen-collapseSeven"
                  aria-expanded="false"
                  aria-controls="panelsStayOpen-collapseSeven"
                >
                  <h4 className="event-alert p-0 mb-0">Sales Groups</h4>
                </button>
              </div>
              <div
                id="panelsStayOpen-collapseSeven"
                className="accordion-collapse collapse"
                aria-labelledby="panelsStayOpen-headingSeven"
              >
                <div className="">
                  <div className="row mx-0">
                    <div className="container px-0 d-flex">
                      <div className="shadowclass mt-0">
                        <div className="select-comparison d-flex">
                          <label>Input Subsets</label>
                          <Select
                            className="w-100"
                            value={saleGroupSubInput}
                            options={
                              checkNull(
                                finance_sale_details?.theMatrices?.[4],
                                false
                              )
                                ? Object.keys(
                                    finance_sale_details?.theMatrices?.[4]
                                  )
                                    .filter((p) => p == 1)
                                    .map((matrix) => {
                                      return {
                                        value: matrix,
                                        label:
                                          finance_sale_details
                                            ?.theMatrices?.[4]?.[matrix],
                                      };
                                    })
                                : []
                            }
                            isMulti
                            isDisabled
                          />
                        </div>
                        <div className="table-responsive">
                          <table className="checklist-table remove-space inventory_table">
                            <thead>
                              <tr>
                                <th className="second-th" width="">
                                  <a href="#_" className="">
                                    Group Name{" "}
                                  </a>
                                </th>
                                <th> Added/Subtracted From Gross Sales </th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th className="action-td">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkArray(
                                finance_sale_details?.salesGroupsUneditable
                              ) &&
                                finance_sale_details?.salesGroupsUneditable.map(
                                  (saleGroup) => {
                                    return (
                                      <tr>
                                        <td>
                                          {saleGroup?.sales_group_name?.name}{" "}
                                        </td>
                                        <td>
                                          {checkNull(
                                            saleGroup?.action_id,
                                            false
                                          )
                                            ? finance_sale_details
                                                ?.salesGroupNamesActionsAll?.[
                                                saleGroup?.action_id
                                              ]
                                            : ""}
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td className="d-flex justify-content-center">
                                          {getSaleGroupAction(saleGroup)}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                              {checkArray(finance_sale_details?.salesGroups) &&
                                finance_sale_details?.salesGroups.map(
                                  (saleGroup) => {
                                    return (
                                      <tr>
                                        <td>
                                          {saleGroup?.sales_group_name?.name}{" "}
                                        </td>
                                        <td>
                                          {checkNull(
                                            saleGroup?.action_id,
                                            false
                                          )
                                            ? finance_sale_details
                                                ?.salesGroupNamesActionsAll?.[
                                                saleGroup?.action_id
                                              ]
                                            : ""}
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td className="d-flex justify-content-center">
                                          {getSaleGroupAction(saleGroup)}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                            </tbody>
                          </table>
                        </div>
                          <div className="my-3">
                            <button
                              className="btn green-btn-revert add-plus addSp"
                              id="addCount"
                              type="button"
                              onClick={() => closeSaleGroupModal(true)}
                            >
                              <i className="i-icon"> </i> Add Sales Groups
                            </button>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <div
                className="accordion-header"
                id="panelsStayOpen-headingEight"
              >
                <button
                  className="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#panelsStayOpen-collapseEight"
                  aria-expanded="false"
                  aria-controls="panelsStayOpen-collapseEight"
                >
                  <h4 className="event-alert p-0 mb-0">Channels</h4>
                </button>
              </div>
              <div
                id="panelsStayOpen-collapseEight"
                className="accordion-collapse collapse"
                aria-labelledby="panelsStayOpen-headingEight"
              >
                <div className="">
                  <div className="row mx-0">
                    <div className="container px-0 d-flex">
                      <div className="shadowclass mt-0">
                        <div className="table-responsive">
                          <table className="checklist-table remove-space inventory_table">
                            <thead>
                              <tr>
                                <th> Sales Type </th>
                                <th> Channel Name </th>
                                <th> Integration Types </th>
                                <th> Integration Name </th>
                                <th className="action-td"> Actions </th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkArray(finance_sale_details?.channels) &&
                                finance_sale_details?.channels.map(
                                  (channel) => {
                                    return (
                                      <tr>
                                        <td>
                                          {channel?.sales_type?.type_name ?? ""}
                                        </td>
                                        <td>{channel?.channel_name ?? ""} </td>
                                        <td>NA</td>
                                        <td>
                                          {channel?.integration_name ?? ""}{" "}
                                        </td>
                                        <td className="d-flex justify-content-center">
                                          {getChannelAction(channel)}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                            </tbody>
                          </table>
                        </div>
                          <div className="my-3">
                            <button
                              className="btn green-btn-revert add-plus addSp"
                              id="addCount"
                              type="button"
                              onClick={() => closeChannelModal(true)}
                            >
                              <i className="i-icon"> </i> Add Channels
                            </button>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModelContainer
        title={`${
          payMethodInputForm?.id != "" ? "Edit" : "Add"
        } Payment Method`}
        open={payMethodModal}
        closeModal={closePayMethodModal}
        submitModal={payMethodSubmit(submitPayMethodForm)}
        is_loading={is_save_payment_method_loading}
      >
        <form
          class="validate_form"
          id="PaymentMethodGeneralSettingSalesForm"
          accept-charset="utf-8"
        >
          {console.log("payMethodErrors:", payMethodErrors)}
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label for="PaymentMethodMethodName" class="required">
                  Method Name
                </label>{" "}
                <div class="input-wrap">
                  <input
                    class="required"
                    placeholder="Method Name"
                    maxlength="50"
                    type="text"
                    id="PaymentMethodMethodName"
                    {...payMethodReg("method_name")}
                    value={payMethodInputForm?.method_name}
                    onChange={(e) =>
                      changePayMethodInputHandler(
                        "method_name",
                        e?.target?.value ?? ""
                      )
                    }
                  />{" "}
                  <ErrorContainer errors={payMethodErrors} name="method_name" />
                </div>
              </li>
              <li>
                <label for="PaymentMethodMethodTypeId" class="required">
                  Method Type
                </label>{" "}
                <div class="input-wrap select-div">
                  <select
                    name="data[PaymentMethod][method_type_id]"
                    class="required payment_method_type_id"
                    id="PaymentMethodMethodTypeId"
                    readonly={payMethodInputForm?.id != ""}
                    {...payMethodReg("method_type_id")}
                    value={payMethodInputForm?.method_type_id}
                    onChange={(e) =>
                      changePayMethodInputHandler(
                        "method_type_id",
                        e?.target?.value ?? ""
                      )
                    }
                  >
                    <option value="">Select</option>
                    {checkNull(finance_sale_details?.methodTypes, false) &&
                      Object.keys(finance_sale_details?.methodTypes).map(
                        (type) => {
                          return (
                            <option value={type}>
                              {finance_sale_details?.methodTypes?.[type]}
                            </option>
                          );
                        }
                      )}
                  </select>{" "}
                  <ErrorContainer
                    errors={payMethodErrors}
                    name="method_type_id"
                  />
                </div>
              </li>
              {checkPayMethodId(["5"]) && (
                <div class="ar_arvoucher">
                  <li>
                    <label for="PaymentMethodCustomerId" class="required">
                      Account Receivable Customer
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][customer_id]"
                        class="required account_receivable_customer"
                        id="PaymentMethodCustomerId"
                        {...payMethodReg("customer_id")}
                        value={payMethodInputForm?.customer_id}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "customer_id",
                            e?.target?.value ?? ""
                          )
                        }
                        readonly={payMethodInputForm?.id != ""}
                      >
                        <option value="">Select</option>
                        {checkArray(finance_sale_details?.customers) &&
                          (finance_sale_details?.customers).map((cust) => {
                            return (
                              <option value={cust?.id}>
                                {cust?.full_name}
                              </option>
                            );
                          })}
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="customer_id"
                      />
                    </div>
                  </li>
                  <li>
                    <label for="PaymentMethodVoucherGroupId" class="required">
                      Voucher Group
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][voucher_group_id]"
                        class="required voucher_group_group replace_array_data"
                        id="PaymentMethodVoucherGroupId"
                        {...payMethodReg("voucher_group_id")}
                        value={payMethodInputForm?.voucher_group_id}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "voucher_group_id",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="">Select</option>
                        {checkArray(finance_sale_details?.voucherGroups) &&
                          (finance_sale_details?.voucherGroups).map((type) => {
                            return (
                              <option value={type?.id}>{type?.name}</option>
                            );
                          })}
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="voucher_group_id"
                      />
                    </div>
                  </li>
                  <li>
                    <label
                      for="PaymentMethodDefaultArDebitAccountId"
                      class="required"
                    >
                      Default AR Debit Account
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][default_ar_debit_account_id]"
                        class="required"
                        id="PaymentMethodDefaultArDebitAccountId"
                        {...payMethodReg("default_ar_debit_account_id")}
                        value={payMethodInputForm?.default_ar_debit_account_id}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "default_ar_debit_account_id",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="">Select</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="default_ar_debit_account_id"
                      />
                    </div>
                  </li>
                  <li>
                    <label
                      for="PaymentMethodDefaultCustomerPaymentAccountId"
                      class="required"
                    >
                      Default Final Customer Payment Account
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][default_customer_payment_account_id]"
                        class="required"
                        id="PaymentMethodDefaultCustomerPaymentAccountId"
                        {...payMethodReg("default_customer_payment_account_id")}
                        value={
                          payMethodInputForm?.default_customer_payment_account_id
                        }
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "default_customer_payment_account_id",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="">Select</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="default_customer_payment_account_id"
                      />
                    </div>
                  </li>
                  <li>
                    <label
                      for="PaymentMethodFixedAmountForVouchers"
                      class="required"
                    >
                      Fixed Amount For Vouchers?
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][fixed_amount_for_vouchers]"
                        class="required display_voucher_value"
                        id="PaymentMethodFixedAmountForVouchers"
                        {...payMethodReg("fixed_amount_for_vouchers")}
                        value={payMethodInputForm?.fixed_amount_for_vouchers}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "fixed_amount_for_vouchers",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="fixed_amount_for_vouchers"
                      />
                    </div>
                  </li>
                  {payMethodInputForm?.fixed_amount_for_vouchers == "yes" && (
                    <li class="voucher_value_fld">
                      <label for="PaymentMethodVoucherValue" class="required">
                        Voucher Value
                      </label>{" "}
                      <div class="input-wrap">
                        <input
                          name="data[PaymentMethod][voucher_value]"
                          class="required currency"
                          placeholder="Voucher Value"
                          type="text"
                          id="PaymentMethodVoucherValue"
                          {...payMethodReg("voucher_value")}
                          value={payMethodInputForm?.voucher_value}
                          onChange={(e) =>
                            changePayMethodInputHandler(
                              "voucher_value",
                              e?.target?.value ?? ""
                            )
                          }
                        />{" "}
                        <ErrorContainer
                          errors={payMethodErrors}
                          name="voucher_value"
                        />
                      </div>
                    </li>
                  )}

                  <li>
                    <label
                      for="PaymentMethodCaptureExcessAmountOtherIncome"
                      class="required"
                    >
                      Capture Excess Amount as Other Income?
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][capture_excess_amount_other_income]"
                        class="required"
                        id="PaymentMethodCaptureExcessAmountOtherIncome"
                        {...payMethodReg("capture_excess_amount_other_income")}
                        value={
                          payMethodInputForm?.capture_excess_amount_other_income
                        }
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "capture_excess_amount_other_income",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="capture_excess_amount_other_income"
                      />
                    </div>
                  </li>
                </div>
              )}
              {checkPayMethodId(["", "1", "2", "3", "4"]) && (
                <>
                  <li class="without_voucher">
                    <label for="PaymentMethodMethodFee" class="required">
                      Method Fee
                    </label>{" "}
                    <div class="input-wrap">
                      <input
                        name="data[PaymentMethod][method_fee]"
                        class="required percent_format"
                        placeholder="Method Fee"
                        type="text"
                        id="PaymentMethodMethodFee"
                        {...payMethodReg("method_fee")}
                        value={payMethodInputForm?.method_fee}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "method_fee",
                            e?.target?.value ?? ""
                          )
                        }
                        readonly={payMethodInputForm?.id != ""}
                      />{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="method_fee"
                      />
                    </div>
                  </li>
                  <li class="without_voucher">
                    <label for="PaymentMethodRefundable" class="required">
                      Refundable?
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][refundable]"
                        class="required payment_refundable"
                        id="PaymentMethodRefundable"
                        {...payMethodReg("refundable")}
                        value={payMethodInputForm?.refundable}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "refundable",
                            e?.target?.value ?? ""
                          )
                        }
                        readonly={payMethodInputForm?.id != ""}
                      >
                        <option value="1">Yes</option>
                        <option value="2">No</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="refundable"
                      />
                    </div>
                  </li>
                  <li class="without_voucher">
                    <label class="required">
                      Default End of Day Debit Account
                    </label>
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][default_eod_debit_account_id]"
                        class="required"
                        id="PaymentMethodDefaultEodDebitAccountId"
                        {...payMethodReg("default_eod_debit_account_id")}
                        value={payMethodInputForm?.default_eod_debit_account_id}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "default_eod_debit_account_id",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="1">Custom Auto</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="default_eod_debit_account_id"
                      />
                    </div>
                  </li>
                </>
              )}
              {checkPayMethodId(["1", "2", "3", "4", "6"]) && (
                <li class="applicable">
                  <label class="required">Default Final Debit Account</label>
                  <div class="input-wrap select-div">
                    <select
                      name="data[PaymentMethod][default_debit_account_id]"
                      class="required"
                      id="PaymentMethodDefaultDebitAccountId"
                      {...payMethodReg("default_debit_account_id")}
                      value={payMethodInputForm?.default_debit_account_id}
                      onChange={(e) =>
                        changePayMethodInputHandler(
                          "default_debit_account_id",
                          e?.target?.value ?? ""
                        )
                      }
                    >
                      <option value="">Select</option>
                      {checkArray(finance_sale_details?.total_accounts) &&
                        (finance_sale_details?.total_accounts).map((acc) => {
                          return <option value={acc?.id}>{acc?.name}</option>;
                        })}
                    </select>{" "}
                    <ErrorContainer
                      errors={payMethodErrors}
                      name="default_debit_account_id"
                    />
                  </div>
                </li>
              )}
              {!checkPayMethodId(["5", "6"]) && (
                <li class="sameday_clearing">
                  <label class="required">Same Day Clearing?</label>
                  <div class="input-wrap select-div">
                    <select
                      name="data[PaymentMethod][same_day_clearing]"
                      class="required"
                      id="PaymentMethodSameDayClearing"
                      {...payMethodReg("same_day_clearing")}
                      value={payMethodInputForm?.same_day_clearing}
                      onChange={(e) =>
                        changePayMethodInputHandler(
                          "same_day_clearing",
                          e?.target?.value ?? ""
                        )
                      }
                    >
                      <option value="">Select</option>
                      <option value="1">Yes</option>
                      <option value="2">No</option>
                      <option value="3">Never</option>
                    </select>{" "}
                    <ErrorContainer
                      errors={payMethodErrors}
                      name="same_day_clearing"
                    />
                  </div>
                </li>
              )}
              {payMethodInputForm?.same_day_clearing == 2 && (
                <li class="sameclearing hide">
                  <label>Clearing Delay (Days)</label>
                  <div class="input-wrap select-div">
                    <input
                      name="data[PaymentMethod][clearing_delay]"
                      class="number_format"
                      placeholder="0"
                      type="text"
                      id="PaymentMethodClearingDelay"
                      {...payMethodReg("clearing_delay")}
                      value={payMethodInputForm?.clearing_delay}
                      onChange={(e) =>
                        changePayMethodInputHandler(
                          "clearing_delay",
                          e?.target?.value ?? ""
                        )
                      }
                      readonly={payMethodInputForm?.id != ""}
                    />{" "}
                    <ErrorContainer
                      errors={payMethodErrors}
                      name="clearing_delay"
                    />
                  </div>
                </li>
              )}
              {checkPayMethodId(["6"]) && (
                <div class="ap_apvoucher">
                  <li>
                    <label for="PaymentMethodApVoucherTypeId" class="required">
                      AP Voucher Type
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][ap_voucher_type_id]"
                        class="required voucher_type"
                        id="PaymentMethodApVoucherTypeId"
                        {...payMethodReg("ap_voucher_type_id")}
                        value={payMethodInputForm?.ap_voucher_type_id}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "ap_voucher_type_id",
                            e?.target?.value ?? ""
                          )
                        }
                        readonly={payMethodInputForm?.id != ""}
                      >
                        <option value="">Select</option>
                        <option value="1">Standard Pre-Paid (Multi-Use)</option>
                        <option value="2">
                          Standard Pre-Paid (Single Use)
                        </option>
                        <option value="3">E-Voucher</option>
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="ap_voucher_type_id"
                      />
                    </div>
                  </li>
                  <li>
                    <label for="PaymentMethodVoucherGroupId" class="required">
                      Voucher Group
                    </label>{" "}
                    <div class="input-wrap select-div">
                      <select
                        name="data[PaymentMethod][voucher_group_id]"
                        class="required voucher_group voucher_group_group replace_array_data"
                        id="PaymentMethodVoucherGroupId"
                        {...payMethodReg("voucher_group_id")}
                        value={payMethodInputForm?.voucher_group_id}
                        onChange={(e) =>
                          changePayMethodInputHandler(
                            "voucher_group_id",
                            e?.target?.value ?? ""
                          )
                        }
                      >
                        <option value="">Select</option>
                        {checkArray(finance_sale_details?.voucherGroups) &&
                          (finance_sale_details?.voucherGroups).map((type) => {
                            return (
                              <option value={type?.id}>{type?.name}</option>
                            );
                          })}
                      </select>{" "}
                      <ErrorContainer
                        errors={payMethodErrors}
                        name="voucher_group_id"
                      />
                    </div>
                  </li>
                  {(payMethodInputForm?.ap_voucher_type_id == "1" ||
                    payMethodInputForm?.ap_voucher_type_id == "2") && (
                    <>
                      <li class="e_voucher_type_hide">
                        <label
                          for="PaymentMethodApCreditAccountId"
                          class="required"
                        >
                          Default AP Credit Account
                        </label>{" "}
                        <div class="input-wrap select-div">
                          <select
                            name="data[PaymentMethod][ap_credit_account_id]"
                            class="required"
                            id="PaymentMethodApCreditAccountId"
                            {...payMethodReg("ap_credit_account_id")}
                            value={payMethodInputForm?.ap_credit_account_id}
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "ap_credit_account_id",
                                e?.target?.value ?? ""
                              )
                            }
                          >
                            <option value="">Select</option>
                            {checkNull(group_account, false) &&
                              Object.keys(group_account).map((gAcc) => {
                                return (
                                  <option value={gAcc}>
                                    {group_account?.[gAcc]}
                                  </option>
                                );
                              })}
                          </select>{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="ap_credit_account_id"
                          />
                        </div>
                      </li>
                      <li class="e_voucher_type_hide">
                        <label
                          for="PaymentMethodFundsDebitAccountId"
                          class="required"
                        >
                          Received Funds Debit Account
                        </label>{" "}
                        <div class="input-wrap select-div">
                          <select
                            name="data[PaymentMethod][funds_debit_account_id]"
                            class="required"
                            id="PaymentMethodFundsDebitAccountId"
                            {...payMethodReg("funds_debit_account_id")}
                            value={payMethodInputForm?.funds_debit_account_id}
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "funds_debit_account_id",
                                e?.target?.value ?? ""
                              )
                            }
                          >
                            <option value="">Select</option>
                            {checkArray(finance_sale_details?.cce_accounts) &&
                              (finance_sale_details?.cce_accounts).map(
                                (cce) => {
                                  return (
                                    <option value={cce?.id}>{cce?.name}</option>
                                  );
                                }
                              )}
                          </select>{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="funds_debit_account_id"
                          />
                        </div>
                      </li>
                      <li class="e_voucher_type_hide">
                        <label
                          for="PaymentMethodConvertUnredeemedBalance"
                          class="required"
                        >
                          Auto-Convert Unredeemed Balance to Income
                        </label>{" "}
                        <div class="input-wrap select-div">
                          <select
                            name="data[PaymentMethod][convert_unredeemed_balance]"
                            class="required"
                            id="PaymentMethodConvertUnredeemedBalance"
                            {...payMethodReg("convert_unredeemed_balance")}
                            value={
                              payMethodInputForm?.convert_unredeemed_balance
                            }
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "convert_unredeemed_balance",
                                e?.target?.value ?? ""
                              )
                            }
                          >
                            <option value="">Select</option>
                            <option value="1">Yes</option>
                            <option value="2">No</option>
                          </select>{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="convert_unredeemed_balance"
                          />
                        </div>
                      </li>
                      <li class="e_voucher_type_hide">
                        <label
                          for="PaymentMethodOtherIncomeAccountExpiryId"
                          class="required"
                        >
                          Voucher Other Income Account Upon Expiry
                        </label>{" "}
                        <div class="input-wrap select-div">
                          <select
                            name="data[PaymentMethod][other_income_account_expiry_id]"
                            class="required"
                            id="PaymentMethodOtherIncomeAccountExpiryId"
                            {...payMethodReg("other_income_account_expiry_id")}
                            value={
                              payMethodInputForm?.other_income_account_expiry_id
                            }
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "other_income_account_expiry_id",
                                e?.target?.value ?? ""
                              )
                            }
                          >
                            <option value="">Select</option>
                            {checkArray(finance_sale_details?.cce_accounts) &&
                              (finance_sale_details?.cce_accounts).map(
                                (cce) => {
                                  return (
                                    <option value={cce?.id}>{cce?.name}</option>
                                  );
                                }
                              )}
                          </select>{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="other_income_account_expiry_id"
                          />
                        </div>
                      </li>
                    </>
                  )}
                  {!(
                    payMethodInputForm?.ap_voucher_type_id == "1" ||
                    payMethodInputForm?.ap_voucher_type_id == "2"
                  ) && (
                    <>
                      <li class="e_voucher_type_show">
                        <label
                          for="PaymentMethodVoucherPartner"
                          class="required"
                        >
                          Voucher Partner
                        </label>{" "}
                        <div class="input-wrap select-div">
                          <select
                            name="data[PaymentMethod][voucher_partner]"
                            class="required"
                            id="PaymentMethodVoucherPartner"
                            {...payMethodReg("voucher_partner")}
                            value={payMethodInputForm?.voucher_partner}
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "voucher_partner",
                                e?.target?.value ?? ""
                              )
                            }
                          >
                            <option value="">Select</option>
                            <option value="1">Loyalty</option>
                            <option value="2">Grabfood</option>
                          </select>{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="voucher_partner"
                          />
                        </div>
                      </li>
                      <li class="e_voucher_type_show">
                        <label
                          for="PaymentMethodVoucherSeriesId"
                          class="required"
                        >
                          Voucher Series ID
                        </label>{" "}
                        <div class="input-wrap">
                          <input
                            name="data[PaymentMethod][voucher_series_id]"
                            class="required"
                            type="text"
                            id="PaymentMethodVoucherSeriesId"
                            {...payMethodReg("voucher_series_id")}
                            value={payMethodInputForm?.voucher_series_id}
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "voucher_series_id",
                                e?.target?.value ?? ""
                              )
                            }
                          />{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="voucher_series_id"
                          />
                        </div>
                      </li>
                    </>
                  )}
                  {(payMethodInputForm?.ap_voucher_type_id == "1" ||
                    payMethodInputForm?.ap_voucher_type_id == "2") && (
                    <>
                      <li class="e_voucher_type_hide">
                        <label
                          for="PaymentMethodVoucherTotalValue"
                          class="required"
                        >
                          Voucher Total Value
                        </label>{" "}
                        <div class="input-wrap">
                          <input
                            name="data[PaymentMethod][voucher_total_value]"
                            class="required currency total_voucher_value"
                            readonly="readonly"
                            type="text"
                            id="PaymentMethodVoucherTotalValue"
                            {...payMethodReg("voucher_total_value")}
                            value={payMethodInputForm?.voucher_total_value}
                            onChange={(e) =>
                              changePayMethodInputHandler(
                                "voucher_total_value",
                                e?.target?.value ?? ""
                              )
                            }
                          />{" "}
                          <ErrorContainer
                            errors={payMethodErrors}
                            name="voucher_total_value"
                          />
                        </div>
                      </li>
                      <li class="e_voucher_type_hide">
                        <div class="overflow-table">
                          <table
                            class="table checklist-table remove-space"
                            id="denomination_table"
                          >
                            <thead>
                              <tr>
                                <th> Denom Value </th>
                                <th> # of Denom </th>
                                <th> Total $ Denom </th>
                                <th> Expiry Date </th>
                                <th> AR Inv. ID </th>
                              </tr>
                            </thead>
                            <tbody id="denomination_list">
                              {checkArray(
                                payMethodInputForm?.denomination_table
                              ) &&
                                payMethodInputForm?.denomination_table.map(
                                  (denom, denomInx) => {
                                    return (
                                      <tr rowcount="1">
                                        <td>
                                          {" "}
                                          <input
                                            {...payMethodReg(
                                              `denomination_table.${denomInx}.denom_value`
                                            )}
                                            class="required currency denom_value valid"
                                            type="text"
                                            onChange={(e) =>
                                              changeDenominationHandler(
                                                denomInx,
                                                "denom_value",
                                                e?.target?.value ?? ""
                                              )
                                            }
                                            value={denom?.denom_value}
                                          />{" "}
                                        </td>
                                        <td>
                                          {" "}
                                          <input
                                            {...payMethodReg(
                                              `denomination_table.${denomInx}.denom_number`
                                            )}
                                            class="number_format denom_number valid"
                                            type="text"
                                            onChange={(e) =>
                                              changeDenominationHandler(
                                                denomInx,
                                                "denom_number",
                                                e?.target?.value ?? ""
                                              )
                                            }
                                            value={denom?.denom_number}
                                          />{" "}
                                        </td>
                                        <td>
                                          {" "}
                                          <input
                                            {...payMethodReg(
                                              `denomination_table.${denomInx}.total_denom`
                                            )}
                                            class="required currency total_denom valid"
                                            readonly
                                            type="text"
                                            value={denom?.total_denom}
                                          />{" "}
                                        </td>
                                        <td>
                                          {" "}
                                          <DatePicker
                                            {...payMethodReg(
                                              `denomination_table.${denomInx}.expiry_date`
                                            )}
                                            autoComplete="new-password"
                                            dateFormat="yyyy-MM-dd"
                                            onChange={(val) =>
                                              changeDenominationHandler(
                                                denomInx,
                                                "expiry_date",
                                                val
                                              )
                                            }
                                            selected={denom?.expiry_date}
                                          />
                                        </td>
                                        <td>
                                          {" "}
                                          <div class="select-div">
                                            <select
                                              {...payMethodReg(
                                                `denomination_table.${denomInx}.ar_invoice_id`
                                              )}
                                              class="valid"
                                              onChange={(e) =>
                                                changeDenominationHandler(
                                                  denomInx,
                                                  "ar_invoice_id",
                                                  e?.target?.value ?? ""
                                                )
                                              }
                                              value={denom?.ar_invoice_id}
                                            >
                                              <option value="">Select</option>
                                              <option value="1">
                                                ARIHAR000001
                                              </option>
                                              <option value="2">
                                                ARIHAR000002
                                              </option>
                                              <option value="3">
                                                ARIHAR000003
                                              </option>
                                            </select>
                                          </div>{" "}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ margin: "15px 0" }} class="btn-row">
                          <button
                            type="button"
                            class="btn green-btn-revert add_denomination"
                            onClick={() => addDenominationHandler()}
                          >
                            {" "}
                            Add Denomination{" "}
                          </button>
                        </div>
                      </li>
                    </>
                  )}
                </div>
              )}
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`${
          servicePeriodInputForm?.id != "" ? "Edit" : "Add"
        } Service Period`}
        open={servicePeriodModal}
        closeModal={closeServicePeriodModal}
        submitModal={servicePeriodSubmit(submitServicePeriodForm)}
        is_loading={is_service_period_loading}
      >
        <form
          class="validate_form FeatureAddDescriptionForm"
          id="ServicePeriodGeneralSettingSalesForm"
          method="post"
          accept-charset="utf-8"
          novalidate="novalidate"
        >
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label for="ServicePeriodPeriodName" class="required">
                  Period Name
                </label>{" "}
                <div class="input-wrap">
                  <input
                    {...servicePeriodReg("period_name")}
                    class="required"
                    placeholder="Period Name"
                    type="text"
                    id="ServicePeriodPeriodName"
                    value={servicePeriodInputForm?.period_name}
                    onChange={(e) =>
                      changeServicePeriodInputHandler(
                        "period_name",
                        e?.target?.value ?? ""
                      )
                    }
                  />{" "}
                  <ErrorContainer
                    errors={servicePeriodErrors}
                    name="period_name"
                  />
                </div>
              </li>
              <li>
                <label for="ServicePeriodPeriodDays" class="required">
                  Period Days
                </label>{" "}
                <Select
                  className="w-100"
                  {...servicePeriodReg("period_days")}
                  value={servicePeriodInputForm?.period_days}
                  onChange={(values) =>
                    changeServicePeriodInputHandler("period_days", values)
                  }
                  options={weeklyArr}
                  isMulti
                  // isDisabled={inputForm?.id !== ""}
                />
                <ErrorContainer
                  errors={servicePeriodErrors}
                  name="period_days"
                />
              </li>
              <li className="d-flex">
                <div className="col-6">
                  <label for="ServicePeriodStartTime" class="required">
                    Period Start
                  </label>{" "}
                  <div className="timePicker">
                    <TimePicker
                      timeMode="12"
                      minuteStep={30}
                      time={servicePeriodInputForm.start_time}
                      onTimeChange={(options) => {
                        const { hour, minute, meridiem } = options;
                        changeServicePeriodInputHandler(
                          "start_time",
                          `${hour}:${minute} ${meridiem}`
                        );
                      }}
                    />
                    <ErrorContainer
                      errors={servicePeriodErrors}
                      name="start_time"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <label for="ServicePeriodEndTime" class="required">
                    Period End
                  </label>{" "}
                  <div className="timePicker">
                    <TimePicker
                      timeMode="12"
                      minuteStep={30}
                      time={servicePeriodInputForm.end_time}
                      onTimeChange={(options) => {
                        const { hour, minute, meridiem } = options;
                        changeServicePeriodInputHandler(
                          "end_time",
                          `${hour}:${minute} ${meridiem}`
                        );
                      }}
                    />
                    <ErrorContainer
                      errors={servicePeriodErrors}
                      name="end_time"
                    />
                  </div>
                </div>
              </li>
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`${saleTypeInputForm?.id != "" ? "Edit" : "Add"} Sales Type`}
        open={saleTypeModal}
        closeModal={closeSaleTypeModal}
        submitModal={saleTypeSubmit(submitSaleTypeForm)}
        is_loading={is_sale_type_loading}
      >
        <form
          class="validate_form"
          id="SalesTypeGeneralSettingSalesForm"
          method="post"
          accept-charset="utf-8"
          novalidate="novalidate"
        >
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label for="SalesTypeTypeName" class="required">
                  Type Name
                </label>{" "}
                <div class="input-wrap">
                  <input
                    {...saleTypeReg("type_name")}
                    class="required"
                    placeholder="Type Name"
                    maxlength="50"
                    type="text"
                    id="SalesTypeTypeName"
                    value={saleTypeInputForm?.type_name}
                    onChange={(e) =>
                      changeSaleTypeInputHandler(
                        "type_name",
                        e?.target?.value ?? ""
                      )
                    }
                  />{" "}
                  <ErrorContainer errors={saleTypeErrors} name="type_name" />
                </div>
              </li>
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`${saleGroupInputForm?.id != "" ? "Edit" : "Add"} Sales Group`}
        open={saleGroupModal}
        closeModal={closeSaleGroupModal}
        submitModal={saleGroupSubmit(submitSaleGroupForm)}
        is_loading={is_sale_group_loading}
      >
        <form
          class="validate_form"
          id="SalesGroupGeneralSettingSalesForm"
          method="post"
          accept-charset="utf-8"
          novalidate="novalidate"
        >
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label for="SalesGroupSalesGroupNameId" class="required">
                  Group Name
                </label>{" "}
                <div class="input-wrap select-div">
                  <select
                    class="required sales_group_name"
                    id="SalesGroupSalesGroupNameId"
                    {...saleGroupReg("sales_group_name_id")}
                    value={saleGroupInputForm?.sales_group_name_id}
                    onChange={(e) =>
                      changeSaleGroupInputHandler(
                        "sales_group_name_id",
                        e?.target?.value ?? ""
                      )
                    }
                  >
                    <option value="">Select Group Name</option>
                    {checkArray(finance_sale_details?.salesGroupNames) &&
                      finance_sale_details?.salesGroupNames.map((saleGroup) => {
                        return (
                          <option value={saleGroup?.id}>
                            {saleGroup?.name}
                          </option>
                        );
                      })}
                  </select>{" "}
                  <ErrorContainer
                    errors={saleGroupErrors}
                    name="sales_group_name_id"
                  />
                </div>
              </li>
              <li>
                <label for="SalesGroupActionId" class="required">
                  Added/Subtracted From Gross Sales
                </label>
                <div class="input-wrap select-div">
                  <select
                    class="required sales_group_action"
                    disabled="disabled"
                    id="SalesGroupActionId"
                    {...saleGroupReg("action_id")}
                    value={saleGroupInputForm?.action_id}
                  >
                    <option value="">Select</option>
                    {checkNull(
                      finance_sale_details?.salesGroupNamesActionsAll,
                      false
                    ) &&
                      Object.keys(
                        finance_sale_details?.salesGroupNamesActionsAll
                      ).map((groupName) => {
                        return (
                          <option value={groupName}>
                            {
                              finance_sale_details?.salesGroupNamesActionsAll?.[
                                groupName
                              ]
                            }
                          </option>
                        );
                      })}
                  </select>{" "}
                  <ErrorContainer errors={saleGroupErrors} name="action_id" />
                </div>
              </li>
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`${channelInputForm?.id != "" ? "Edit" : "Add"} Channel`}
        open={channelModal}
        closeModal={closeChannelModal}
        submitModal={channelSubmit(submitChannelForm)}
        is_loading={is_channel_loading}
      >
        <form
          class="validate_form"
          id="ChannelGeneralSettingSalesForm"
          method="post"
          accept-charset="utf-8"
          novalidate="novalidate"
        >
          <fieldset class="">
            <ul class="form-list label-group new-form">
              <li>
                <label for="ChannelSalesTypeId" class="required">
                  Sales Type
                </label>{" "}
                <div class="input-wrap select-div">
                  <select
                    name="data[Channel][sales_type_id]"
                    class="required valid"
                    id="ChannelSalesTypeId"
                    {...channelReg("sales_type_id")}
                    value={channelInputForm?.sales_type_id}
                    onChange={(e) =>
                      changeChannelInputHandler(
                        "sales_type_id",
                        e?.target?.value ?? ""
                      )
                    }
                  >
                    <option value="">Select</option>
                    {checkArray(finance_sale_details?.sales_types) &&
                      finance_sale_details?.sales_types.map((sType) => {
                        return (
                          <option value={sType?.id}>{sType.type_name}</option>
                        );
                      })}
                  </select>{" "}
                  <ErrorContainer
                    errors={saleGroupErrors}
                    name="sales_type_id"
                  />
                </div>
              </li>
              <li>
                <label for="ChannelChannelName" class="required">
                  Channel Name
                </label>{" "}
                <div class="input-wrap">
                  <input
                    name="data[Channel][channel_name]"
                    class="required"
                    placeholder="Enter Channel Name"
                    maxlength="200"
                    type="text"
                    id="ChannelChannelName"
                    {...channelReg("channel_name")}
                    value={channelInputForm?.channel_name}
                    onChange={(e) =>
                      changeChannelInputHandler(
                        "channel_name",
                        e?.target?.value ?? ""
                      )
                    }
                  />{" "}
                  <ErrorContainer
                    errors={saleGroupErrors}
                    name="channel_name"
                  />
                </div>
              </li>
              <li>
                <label for="ChannelIntegrationType" class="required">
                  Integration Type
                </label>{" "}
                <div class="input-wrap select-div">
                  <select
                    name="data[Channel][integration_type]"
                    class="required valid"
                    id="ChannelIntegrationType"
                    {...channelReg("integration_type")}
                    value={channelInputForm?.integration_type}
                    onChange={(e) =>
                      changeChannelInputHandler(
                        "integration_type",
                        e?.target?.value ?? ""
                      )
                    }
                  >
                    <option value="">Select</option>
                    <option value="1">NA</option>
                  </select>{" "}
                  <ErrorContainer
                    errors={saleGroupErrors}
                    name="integration_type"
                  />
                </div>
              </li>
              <li>
                <label for="ChannelIntegrationName" class="required">
                  Integration Name
                </label>{" "}
                <div class="input-wrap">
                  <input
                    name="data[Channel][integration_name]"
                    class="required"
                    placeholder="Enter Integration Name"
                    maxlength="200"
                    type="text"
                    id="ChannelIntegrationName"
                    {...channelReg("integration_name")}
                    value={channelInputForm?.integration_name}
                    onChange={(e) =>
                      changeChannelInputHandler(
                        "integration_name",
                        e?.target?.value ?? ""
                      )
                    }
                  />{" "}
                  <ErrorContainer
                    errors={saleGroupErrors}
                    name="integration_name"
                  />
                </div>
              </li>
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
    </div>
  );
};
export default Sales;
